import store from '../store.js'
import api from '../api.js'
import { busy, notBusy } from '../busy/actions'
import { handleGetErrors, handleUpdateErrors } from '../error/actions'
import { estimateTotalWithLookAhead, getPrefetchPagesAhead } from '../pagination/shared.js'

export const DEPARTMENT_UPDATE_ACTION = 'DEPARTMENT_UPDATE_ACTION'
export const DEPARTMENT_DELETE_ACTION = 'DEPARTMENT_DELETE_ACTION'
export const DEPARTMENT_EXPAND_NODE_ACTION = 'DEPARTMENT_EXPAND_NODE_ACTION'

export const DEPARTMENT_GET_PAGE_ITEMS_ACTION = 'DEPARTMENT_GET_PAGE_ITEMS_ACTION'
export const DEPARTMENT_GET_CATEGORIES_ACTION = 'DEPARTMENT_GET_CATEGORIES_ACTION'
export const DEPARTMENT_SET_ORDER_ACTION = 'DEPARTMENT_SET_ORDER_ACTION'
export const DEPARTMENT_SET_LIMIT_ACTION = 'DEPARTMENT_SET_LIMIT_ACTION'
export const DEPARTMENT_SET_OFFSET_ACTION = 'DEPARTMENT_SET_OFFSET_ACTION'
export const DEPARTMENT_SET_SEARCH_ACTION = 'DEPARTMENT_SET_SEARCH_ACTION'
export const DEPARTMENT_SET_TOTAL_COUNT_ACTION = 'DEPARTMENT_SET_TOTAL_COUNT_ACTION'
export const DEPARTMENT_TOGGLE_SHOW_FAVORITES = 'DEPARTMENT_TOGGLE_SHOW_FAVORITES'
export const DEPARTMENT_TOGGLE_SHOW_ACTIVE = 'DEPARTMENT_TOGGLE_SHOW_ACTIVE'
export const DEPARTMENT_TOGGLE_SHOW_RECENT = 'DEPARTMENT_TOGGLE_SHOW_RECENT'

const MAX_PREFETCH_ITEMS = 200
const departmentPageCache = new Map()
const departmentPageRequestsInFlight = new Map()

function invalidateDepartmentPageCache () {
  departmentPageCache.clear()
  departmentPageRequestsInFlight.clear()
}

export function departmentUpdateAction (department) {
  return {
    type: DEPARTMENT_UPDATE_ACTION,
    department
  }
}

export function departmentDeleteAction (department) {
  return {
    type: DEPARTMENT_DELETE_ACTION,
    department
  }
}

export function getPageItems () {
  const state = store.getState()
  const {
    search,
    limit,
    offset,
    orderBy,
    order,
    showFavorites,
    showActive,
    showRecent
  } = state.department

  const sort = orderBy ? `${orderBy}.${order}` : null
  const params = {
    query: [],
    limit,
    offset,
    sort,
    recent: showRecent
  }

  if (typeof search === 'string' && search.length > 0) {
    params.query.push({ key: 'name', value: search, op: '~' })
  }

  const hasSearch = typeof search === 'string' && search.trim().length > 0

  if (showFavorites && !hasSearch) {
    params.query.push({ key: 'is_favorite', value: showFavorites, op: '=' })
  }
  applyActiveFilter(params.query, showActive)

  return function (dispatch) {
    const limitNumber = Math.max(1, Number(limit) || 1)
    const offsetNumber = Math.max(0, Number(offset) || 0)
    const cacheKey = getDepartmentCacheKey({ query: params.query, recent: params.recent, sort: params.sort, limit: limitNumber })
    const cachedPage = getDepartmentCachedPage(cacheKey, offsetNumber)
    const cachedTotal = getDepartmentCachedTotal(cacheKey)

    if (cachedPage) {
      dispatch({
        type: DEPARTMENT_GET_PAGE_ITEMS_ACTION,
        pageItems: cachedPage,
        totalCount: Math.max(Number(cachedTotal) || 0, offsetNumber + cachedPage.length)
      })
      setTimeout(() => {
        prefetchDepartmentPagesAhead(cacheKey, {
          query: params.query,
          recent: params.recent,
          sort: params.sort,
          limit: limitNumber
        }, offsetNumber)
      }, 0)
      return
    }

    dispatch(busy())
    fetchDepartmentsPageWithCache({
      query: params.query,
      recent: params.recent,
      sort: params.sort,
      limit: limitNumber,
      offset: offsetNumber
    }, cacheKey).then(({ pageItems, totalCount }) => {
      const initialTotal = Math.max(Number(totalCount) || 0, offsetNumber + pageItems.length)

      dispatch(notBusy())
      dispatch({
        type: DEPARTMENT_GET_PAGE_ITEMS_ACTION,
        pageItems,
        totalCount: initialTotal
      })

      setTimeout(() => {
        prefetchDepartmentPagesAhead(cacheKey, {
          query: params.query,
          recent: params.recent,
          sort: params.sort,
          limit: limitNumber
        }, offsetNumber)

        estimateTotalWithLookAhead(
          (nextParams) => fetchDepartmentsPageWithCache(nextParams, cacheKey),
          { query: params.query, recent: params.recent },
          {
            batchSize: limitNumber,
            firstPageItems: pageItems,
            currentOffset: offsetNumber,
            maxPrefetchItems: MAX_PREFETCH_ITEMS,
            maxPagesAhead: 3
          }
        ).then((count) => {
          const safeCount = Math.max(Number(count) || 0, pageItems.length)
          const nextTotal = Math.max(getDepartmentCachedTotal(cacheKey), safeCount)
          setDepartmentCachedTotal(cacheKey, nextTotal)
          dispatch({ type: DEPARTMENT_SET_TOTAL_COUNT_ACTION, totalCount: nextTotal })
        }).catch(() => {})
      }, 0)
    }).catch(handleGetErrors('department', dispatch))
  }
}

function getDepartmentCacheKey ({ query, recent, sort, limit }) {
  return JSON.stringify({
    query: Array.isArray(query) ? query : [],
    recent: !!recent,
    sort: sort || '',
    limit: Math.max(1, Number(limit) || 1)
  })
}

function getDepartmentCacheBucket (cacheKey) {
  if (!departmentPageCache.has(cacheKey)) {
    departmentPageCache.set(cacheKey, { pages: new Map(), totalCount: 0 })
  }
  return departmentPageCache.get(cacheKey)
}

function getDepartmentCachedPage (cacheKey, offset) {
  const bucket = departmentPageCache.get(cacheKey)
  return bucket?.pages?.get(offset) || null
}

function setDepartmentCachedPage (cacheKey, offset, pageItems) {
  const bucket = getDepartmentCacheBucket(cacheKey)
  bucket.pages.set(offset, Array.isArray(pageItems) ? pageItems : [])
}

function getDepartmentCachedTotal (cacheKey) {
  const bucket = departmentPageCache.get(cacheKey)
  return Number(bucket?.totalCount) || 0
}

function setDepartmentCachedTotal (cacheKey, totalCount) {
  const bucket = getDepartmentCacheBucket(cacheKey)
  bucket.totalCount = Math.max(Number(totalCount) || 0, 0)
}

function fetchDepartmentsPageWithCache (params, cacheKey) {
  const limit = Math.max(1, Number(params?.limit) || 1)
  const offset = Math.max(0, Number(params?.offset) || 0)
  const requestKey = `${cacheKey}|${offset}|${limit}`
  const existing = departmentPageRequestsInFlight.get(requestKey)
  if (existing) {
    return existing
  }

  const request = api.getDepartments({ ...params, limit, offset }).then((result) => {
    const pageItems = Array.isArray(result?.data?.value) ? result.data.value : []
    const totalCount = Math.max(Number(result?.data?.total_count) || 0, offset + pageItems.length)
    setDepartmentCachedPage(cacheKey, offset, pageItems)
    setDepartmentCachedTotal(cacheKey, totalCount)
    return { pageItems, totalCount }
  }).finally(() => {
    departmentPageRequestsInFlight.delete(requestKey)
  })

  departmentPageRequestsInFlight.set(requestKey, request)
  return request
}

function prefetchDepartmentPagesAhead (cacheKey, baseParams, currentOffset) {
  const limit = Math.max(1, Number(baseParams?.limit) || 1)
  const pagesAhead = getPrefetchPagesAhead(limit, MAX_PREFETCH_ITEMS, 2)

  if (pagesAhead <= 0) {
    return
  }

  for (let i = 1; i <= pagesAhead; i++) {
    const nextOffset = currentOffset + (i * limit)
    if (getDepartmentCachedPage(cacheKey, nextOffset)) {
      continue
    }

    fetchDepartmentsPageWithCache({ ...baseParams, offset: nextOffset }, cacheKey).catch(() => {})
  }
}

export function getCategories (departmentId) {
  const state = store.getState()
  const { showFavorites, showActive } = state.department

  const query = [{ key: 'collection_id', value: departmentId, op: '=' }]
  if (showFavorites) {
    query.push({ key: 'is_favorite', value: showFavorites, op: '=' })
  }
  applyActiveFilter(query, showActive)

  return function (dispatch) {
    dispatch(busy())
    api.getCategories({ query }).then(result => {
      dispatch(notBusy())
      dispatch({
        type: DEPARTMENT_GET_CATEGORIES_ACTION,
        departmentId,
        categories: result.data.value
      })
    }).catch(handleGetErrors('category', dispatch))
  }
}

export function toggleExpandNode (department) {
  return {
    type: DEPARTMENT_EXPAND_NODE_ACTION,
    department
  }
}

export function toggleFavorite (department) {
  return function (dispatch) {
    const favorite = !department.is_favorite
    const current = store.getState().department.pageItems.find((item) => item.id === department.id) || department
    const updated = { ...current, is_favorite: favorite }
    dispatch(busy())
    api.setDepartmentFavorite(department.id, favorite).then(() => {
      invalidateDepartmentPageCache()
      dispatch(departmentUpdateAction(updated))
      dispatch(notBusy())
    }).catch(handleUpdateErrors('department', dispatch))
  }
}

/**
 * Sort order
 */
export function setOrder (orderBy, order) {
  return {
    type: DEPARTMENT_SET_ORDER_ACTION,
    orderBy,
    order
  }
}

/**
 * Set limit for pagination
 */
export function setLimit (limit) {
  return {
    type: DEPARTMENT_SET_LIMIT_ACTION,
    limit
  }
}

/**
 * Set offset for pagination
 */
export function setOffset (offset) {
  return {
    type: DEPARTMENT_SET_OFFSET_ACTION,
    offset
  }
}

/**
 * Set search
 */
export function setSearch (search) {
  return {
    type: DEPARTMENT_SET_SEARCH_ACTION,
    search
  }
}

/**
 * Toggle show favorites
 */
export function toggleShowFavorites () {
  return {
    type: DEPARTMENT_TOGGLE_SHOW_FAVORITES
  }
}

/**
 * Toggle show active only
 */
export function toggleShowActive () {
  return {
    type: DEPARTMENT_TOGGLE_SHOW_ACTIVE
  }
}

/**
 * Toggle show recent
 */
export function toggleShowRecent () {
  return {
    type: DEPARTMENT_TOGGLE_SHOW_RECENT
  }
}

function applyActiveFilter (query, showActive) {
  if (!showActive) {
    return
  }

  query.push({ key: 'ended_at', value: null, op: '=' })
}
